/* istanbul ignore file */
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Accordion,
  Box,
  Center,
  Divider,
  Heading,
  Skeleton,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { OccupancyType, StudentSelectionMode, SystemTranslationKeys } from '@common/entities';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { CartCharge, CartChargeType } from 'state/slices/shoppingCartSlice';
import { RootState } from 'state/store';

import { Loader } from 'components/Loader';
import { useSystemTranslation } from 'hooks';
import { useStudentSelectionMode } from 'hooks/floor-plans';
import { getUtilities } from 'services';
import { calculateChargeTotal, getDueNowCharges } from 'utils/charge-utils';
import handleError from 'utils/handleError';
import { getInstallmentChargeGroups, isInstallmentCharge } from 'utils/installment-charges';
import { shouldShowCartDetails } from 'utils/shouldShowCartDetails';
import useRefreshScheduledCharges from 'utils/useRefreshScheduledCharges';

import { NewHomeCard } from './new-home-card/NewHomeCard';
import CartItem from './CartItem';
import { ChargesSection } from './ChargesSection';
import { ScheduledChargesError } from './ScheduledChargesError';
import { SpecialsCard } from './SpecialsCard';

interface Props {
  chargesType: CartChargeType | null;
  handleDrawerClose?: () => void;
}

function CartSection({ chargesType, handleDrawerClose }: Props) {
  const { tSystem } = useSystemTranslation();
  const isPreviewMode = useSelector((state: RootState) => state.auth?.isPreviewMode ?? false);
  const { floorPlan, leaseTerm, unit, waitlistSelections } = useSelector(
    (state: RootState) => state.unitSelection,
  );
  const charges = useSelector((state: RootState) => state.shoppingCart.charges);
  const { currencyCode, localeCode } = useSelector((state: RootState) => state.locale);
  const form = useSelector((state: RootState) => state.application.form);
  const applicant = useSelector((state: RootState) => state.application.applicant);
  const response = useSelector((state: RootState) => state.response.value);
  const activeResponse = useSelector((state: RootState) => state.application.activeResponse);
  const { propertyId } = applicant;
  const { responseId, leaseId } = activeResponse;

  const studentSelectionMode = useStudentSelectionMode();
  const isStudentBulkUnitAssignment =
    form.occupancyType === OccupancyType.Student &&
    studentSelectionMode === StudentSelectionMode.BULK_UNIT_ASSIGNMENT;

  const canRenderCartDetails = shouldShowCartDetails({
    isStudentBulkUnitAssignment,
    floorPlanId: floorPlan?.id,
    leaseTerm,
    unit,
    waitlistFloorplanIds: waitlistSelections?.floorplanIds,
    quoteLeaseTermId: response?.quote?.leaseTermId,
    quoteUnitSpaceId: response?.quote?.unitSpaceId,
  });

  const { isSyncingCharges, scheduledChargesSyncError, scheduledChargesRetryLimitReached } =
    useSelector((state: RootState) => state.shoppingCart);
  const { refreshScheduledCharges } = useRefreshScheduledCharges({
    propertyId,
    leaseId,
  });
  const filteredCharges = chargesType ? charges?.filter((c) => c.type === chargesType) : null;
  const installmentGroups = useMemo(() => getInstallmentChargeGroups(charges), [charges]);

  function getAllRecurringCharges() {
    return charges?.filter((charge) => charge.isRecurring === true && !isInstallmentCharge(charge));
  }

  function getChargesDueNow() {
    return getDueNowCharges(charges, tSystem);
  }

  function getChargesDueAtMoveIn() {
    return charges?.filter((charge) => charge.dueAtMoveIn === true && !isInstallmentCharge(charge));
  }

  const {
    isPending,
    data = [],
    error,
    fetchStatus,
  } = useQuery({
    queryKey: ['utility-responsibilities', localeCode],
    queryFn: () => getUtilities(responseId as string),
    enabled: !isPreviewMode,
  });

  useEffect(() => {
    if (error) handleError(`Form Viewer - ${error}`, `${error.message}`);
  }, [error]);

  if (isPending && fetchStatus === 'fetching') return <Loader />;
  return (
    <Box p={{ base: '0 20px', lg: '20px 56px 0 0', sm: '20px 15px' }} position="sticky" top="10px">
      <NewHomeCard handleDrawerClose={handleDrawerClose} />

      {/* Hide the shopping cart details if no unit an leaseTerm have been selected. */}
      {canRenderCartDetails && (
        <div>
          {scheduledChargesSyncError ? (
            <ScheduledChargesError
              compact
              onRetry={() => refreshScheduledCharges(true)}
              isRetrying={isSyncingCharges}
              retryLimitReached={scheduledChargesRetryLimitReached}
            />
          ) : (
            <>
              {!chargesType && (
                <>
                  {data.length > 0 && (
                    <>
                      <p>{tSystem(SystemTranslationKeys.cart.utilityResponsibilities)}</p>
                      <BadgeGroup>
                        {data.map((item, index: number) => {
                          return (
                            <Badge key={`utility-responsibilities-${index}`}>{item.name}</Badge>
                          );
                        })}
                      </BadgeGroup>
                    </>
                  )}
                  {isSyncingCharges ? (
                    <VStack
                      align="stretch"
                      borderRadius="md"
                      spacing={3}
                      my={2}
                      p={2}
                      position="relative"
                    >
                      <Center position="absolute" top="50%" left={0} right={0}>
                        <Text zIndex={500} fontSize="xs">
                          {tSystem(
                            charges.length > 0
                              ? SystemTranslationKeys.cart.updatingCharges
                              : SystemTranslationKeys.cart.loadingCharges,
                          )}
                          ...
                        </Text>
                      </Center>
                      {[0, 1, 2].map((i) => (
                        <Box key={i} borderRadius="md">
                          <Skeleton my={2} height="25px" />
                          <Skeleton height="20px" width="45%" />
                          <Skeleton my={2} height="10px" />
                          <Skeleton my={2} height="10px" />
                          <Skeleton my={2} height="10px" />
                          <Divider />
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Accordion defaultIndex={[0, 1, 2, 3]} allowMultiple>
                      <ChargesSection
                        recurringCharges={getAllRecurringCharges()}
                        dueNow={getChargesDueNow()}
                        dueAtMoveIn={getChargesDueAtMoveIn()}
                        installmentGroups={installmentGroups}
                      />
                    </Accordion>
                  )}
                </>
              )}
              {filteredCharges && filteredCharges.length > 0 && (
                <Box mt={20} borderWidth="2px" borderRadius="lg" padding={3} height={180}>
                  <Heading size="md" fontWeight={500} marginTop={5}>
                    {tSystem(SystemTranslationKeys.cart.chargesDue)}
                  </Heading>

                  <Box mt={2} borderTop="1px solid lightgrey" padding={3}>
                    {filteredCharges.map((charge: CartCharge, i: number) => (
                      <CartItem
                        key={i}
                        label={charge.label}
                        priceNumber={charge.priceNumber}
                        priceFormatted={charge.priceFormatted}
                        currencyCode={currencyCode}
                        isRange={charge.isRange}
                        minPriceNumber={charge.minPriceNumber}
                        maxPriceNumber={charge.maxPriceNumber}
                        amountType={charge.amountType}
                        customFeeAmountType={charge.customFeeAmountType}
                        amountDetail={charge.amountDetail}
                        allChargesDetails={charge.allChargesDetails}
                        arTriggerId={charge.arTriggerId}
                      />
                    ))}
                    <Spacer marginBottom={2} />
                    {(() => {
                      const total = calculateChargeTotal(filteredCharges || [], currencyCode);
                      return (
                        <CartItem
                          key={filteredCharges?.length ?? 0}
                          label={tSystem(SystemTranslationKeys.cart.totalDue)}
                          total
                          priceNumber={total.minTotal}
                          priceFormatted={total.totalFormatted}
                          currencyCode={total.currencyCode}
                          isRange={total.isRange}
                          minPriceNumber={total.minTotal}
                          maxPriceNumber={total.maxTotal}
                          hasVaries={total.hasVaries}
                        />
                      );
                    })()}
                  </Box>
                </Box>
              )}
            </>
          )}
        </div>
      )}
      <SpecialsCard />
    </Box>
  );
}

export default CartSection;

const BadgeGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 5px 0 20px 0;
  margin: 10px 0 0 0;
`;

const Badge = styled.span`
  margin: 5px 5px 0 0;
  background-color: ${({ theme }: any) => theme.colors.gray.light};
  border-radius: 4px;
  display: block;
  padding: 7px 8px;
  font-size: 10px;
`;
